import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { usePageTracking, useAnalytics } from '../hooks/useAnalytics';
import { COURIER_API_ROOT_URL } from '../config';

interface ApplicantInfo {
  fullName: string;
  email: string;
  status: string;
}

const SignContract: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [applicant, setApplicant] = useState<ApplicantInfo | null>(null);
  const [error, setError] = useState('');

  const [agreed, setAgreed] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [signedSuccessfully, setSignedSuccessfully] = useState(false);

  usePageTracking('Sign Contract');
  const { trackContractSigned, trackAPIError } = useAnalytics();

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setError('No contract signing token provided. Please use the link from your approval email.');
      setIsValidating(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(
        `${COURIER_API_ROOT_URL}/api/application/contract/validate-token`,
        { params: { token } }
      );

      if (response.data.success) {
        setIsValid(true);
        setApplicant(response.data.applicant);
      } else {
        setError(response.data.message || 'Invalid or expired contract signing link.');
      }
    } catch (err: any) {
      console.error('Token validation error:', err);
      setError(
        err.response?.data?.message ||
        'Unable to validate contract signing link. Please contact support.'
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleSignContract = async () => {
    if (!agreed) {
      alert('Please check the box to agree to the contract terms.');
      return;
    }

    setIsSigning(true);
    setError('');

    try {
      // Generate PDF client-side
      console.log('Generating contract PDF...');
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = 20;

      // Add header
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Independent Contractor Agreement', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Valley Eats Courier Services', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Add applicant info
      pdf.setFontSize(10);
      pdf.text(`Contractor Name: ${applicant?.fullName}`, margin, yPosition);
      yPosition += 7;
      pdf.text(`Email: ${applicant?.email}`, margin, yPosition);
      yPosition += 7;
      pdf.text(`Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, yPosition);
      yPosition += 12;

      // Add contract content
      const addSection = (title: string, content: string | string[]) => {
        pdf.setFont('helvetica', 'bold');
        pdf.text(title, margin, yPosition);
        yPosition += 7;
        pdf.setFont('helvetica', 'normal');

        const lines = Array.isArray(content) ? content : [content];
        lines.forEach(line => {
          const splitText = pdf.splitTextToSize(line, maxWidth);
          splitText.forEach((textLine: string) => {
            if (yPosition > 270) {
              pdf.addPage();
              yPosition = 20;
            }
            pdf.text(textLine, margin, yPosition);
            yPosition += 5;
          });
        });
        yPosition += 5;
      };

      addSection('1. Position and Reporting', ['You are engaged as an Independent Contractor in the role of Delivery Driver', 'You will report to the Courier Relations Coordinator']);
      addSection('2. Compensation', ['Base Pay: $4.00 per order', 'Performance Bonus: Up to $0.50 per delivery for completing runs within 5 minutes of the system\'s estimated travel time (wait times excluded)', 'Tips: 100% of tips received on deliveries will be paid to you', '', 'Guaranteed Minimum Wage Protection:', 'You are guaranteed Ontario minimum wage for all active delivery time, averaged over a pay-period', 'Active time starts when you mark "En Route" and ends when you mark "Delivered"', 'Minimum wage compliance is calculated across each pay period']);
      addSection('3. Contract Terms', ['This is a contract position with a 90-day probationary period', 'You must provide 24 hours\' notice to drop a scheduled shift unless due to illness, family emergency, or other extenuating circumstances']);
      addSection('4. Independent Contractor Status', ['You are not an employee of Valley Eats', 'You are not eligible for employee benefits', 'You have no authority to bind or represent the Company', 'This agreement does not create an employer-employee relationship']);
      addSection('5. Market Flexibility', ['You may be requested to work in alternate delivery zones based on demand', 'If reassigned, you may receive driver points or financial compensation']);
      addSection('6. Order Assignment System', ['Orders are assigned based on: Distance to pickup, Route efficiency, Acceptance rate, Error rate, Smart bundling', '', 'Important: Background location must be enabled to receive offers', 'Orders are assigned just-in-time before heading to pickup']);
      addSection('7. Performance-Based Rewards', ['Points System: Earn points for positive customer ratings', 'No point deductions: Your account status cannot be downgraded due to performance', 'Points can only increase, safeguarding your ability to receive shift slots and perks']);
      addSection('8. Transparent Earnings Display', ['During assignment: Trip Pay (base + distance) is shown', 'Tips: Displayed only after delivery in accordance with Bill 88', 'After delivery: Complete earnings breakdown is available in-app']);
      addSection('9. Required Equipment', ['You must use a Valley Eats-approved insulated bag', 'Purchase available for $45.00, deducted from your first payout', 'If you withdraw from the contract and do not return the bag, a $60.00 fee will apply']);
      addSection('10. Delivery Requirements', ['A reliable motor-vehicle', 'Maintain a valid driver\'s license', 'Ensure your vehicle is insured', 'Correct delivery of confirmed items is your responsibility']);
      addSection('11. Access to Courier Portal - drivers.valleyeats.ca', ['Access your performance metrics, bonus status, and delivery history', 'Submit and track disputes', 'Monitor your Bill 88 compliance', 'Access performance tips and suggestions', 'Review all timer logs and run history']);
      addSection('12. Dispute Resolution', ['Submit disputes through the Courier Portal', 'A dedicated review team will ensure a timely and transparent process']);
      addSection('13. Comprehensive Audit Protection', ['All delivery and earnings data stored securely for 3+ years', 'Transparent payment calculations', 'Protection against unjust deactivation']);
      addSection('14. At-Will Agreement', ['Either party may terminate at any time, with or without cause', 'Valley Eats may update compensation or terms on a prospective basis with notice']);
      addSection('15. Additional Agreements', ['You may be asked to sign: Confidentiality agreements, Intellectual property or data use agreements']);
      addSection('16. Entire Agreement', 'This document represents the entire agreement between you and Valley Eats. Any changes must be made in writing and signed by both you and an authorized Company representative.');

      // Digital signature
      yPosition += 10;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Digital Signature:', margin, yPosition);
      yPosition += 7;
      pdf.setFont('helvetica', 'italic');
      pdf.text(applicant?.fullName || '', margin, yPosition);
      yPosition += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Signed electronically on ${new Date().toLocaleString('en-US', { timeZone: 'America/Toronto' })}`, margin, yPosition);

      // Convert PDF to base64
      const pdfBase64 = pdf.output('datauristring').split(',')[1];
      console.log('PDF generated, sending to server...');

      // Send to backend
      const response = await axios.post(
        `${COURIER_API_ROOT_URL}/api/application/contract/sign`,
        {
          token,
          agreed: true,
          pdfBase64
        }
      );

      if (response.data.success) {
        setSignedSuccessfully(true);
        trackContractSigned({ signatureType: 'electronic', contractVersion: 'v1' });
      } else {
        setError(response.data.message || 'Failed to sign contract. Please try again.');
      }
    } catch (err: any) {
      console.error('Contract signing error:', err);
      trackAPIError({ endpoint: '/api/application/contract/sign', errorCode: (err as any).response?.status || 'unknown', errorMessage: (err as any).message });
      setError(
        err.response?.data?.message ||
        'Failed to sign contract. Please contact support if this issue persists.'
      );
    } finally {
      setIsSigning(false);
    }
  };

  // Loading state
  if (isValidating) {
    return (
      <>
        <Header />
        <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner" style={{
              width: '50px',
              height: '50px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #79A763',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }}></div>
            <p style={{ fontSize: '16px', color: '#666' }}>Validating contract signing link...</p>
          </div>
        </div>
        <Footer />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  // Error state
  if (!isValid || error) {
    return (
      <>
        <Header />
        <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            maxWidth: '600px',
            padding: '40px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <h2 style={{ color: '#856404', marginBottom: '20px' }}>Unable to Load Contract</h2>
            <p style={{ color: '#856404', marginBottom: '20px' }}>{error}</p>
            <p style={{ color: '#856404', fontSize: '14px' }}>
              If you need assistance, please contact us at{' '}
              <a href="mailto:relations@valleyeats.ca" style={{ color: '#79A763' }}>
                relations@valleyeats.ca
              </a>
            </p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Success state
  if (signedSuccessfully) {
    return (
      <>
        <Header />
        <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            maxWidth: '600px',
            padding: '40px',
            backgroundColor: '#d4edda',
            border: '1px solid #28a745',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>🎉</div>
            <h2 style={{ color: '#155724', marginBottom: '20px' }}>Contract Signed Successfully!</h2>
            <p style={{ color: '#155724', marginBottom: '20px' }}>
              Thank you, {applicant?.fullName}! Your contract has been signed and saved.
            </p>
            <p style={{ color: '#155724', fontSize: '14px' }}>
              Our team will be in touch shortly with your next steps. Welcome to Valley Eats!
            </p>
            <div style={{ marginTop: '30px' }}>
              <a
                href="https://valleyeats.ca"
                style={{
                  display: 'inline-block',
                  padding: '12px 24px',
                  backgroundColor: '#79A763',
                  color: '#ffffff',
                  textDecoration: 'none',
                  borderRadius: '6px',
                  fontWeight: 600
                }}
              >
                Return to Valley Eats
              </a>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Main contract signing view
  return (
    <>
      <Header />
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '40px 20px',
        minHeight: '70vh'
      }}>
        {/* Contract Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '40px',
          paddingBottom: '20px',
          borderBottom: '3px solid #79A763'
        }}>
          <h1 style={{
            color: '#79A763',
            fontSize: '32px',
            marginBottom: '10px'
          }}>
            Independent Contractor Agreement
          </h1>
          <p style={{ color: '#666', fontSize: '16px' }}>
            Valley Eats Courier Services
          </p>
        </div>

        {/* Applicant Info */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '30px'
        }}>
          <p style={{ margin: '5px 0', color: '#333' }}>
            <strong>Contractor Name:</strong> {applicant?.fullName}
          </p>
          <p style={{ margin: '5px 0', color: '#333' }}>
            <strong>Email:</strong> {applicant?.email}
          </p>
          <p style={{ margin: '5px 0', color: '#333' }}>
            <strong>Date:</strong> {new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>

        {/* Contract Content */}
        <div style={{
          backgroundColor: '#ffffff',
          padding: '30px',
          border: '1px solid #ddd',
          borderRadius: '8px',
          marginBottom: '30px',
          maxHeight: '500px',
          overflowY: 'auto'
        }}>
          <ContractContent fullName={applicant?.fullName || ''} />
        </div>

        {/* Agreement Checkbox */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            fontSize: '16px'
          }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              style={{
                width: '20px',
                height: '20px',
                marginRight: '12px',
                cursor: 'pointer'
              }}
            />
            <span style={{ color: '#333' }}>
              I have read, understood, and agree to the terms of this Independent Contractor Agreement.
            </span>
          </label>
        </div>

        {/* Sign Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleSignContract}
            disabled={!agreed || isSigning}
            style={{
              padding: '16px 48px',
              fontSize: '18px',
              fontWeight: 600,
              backgroundColor: agreed ? '#79A763' : '#ccc',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              cursor: agreed ? 'pointer' : 'not-allowed',
              transition: 'all 0.3s ease',
              opacity: isSigning ? 0.6 : 1
            }}
          >
            {isSigning ? 'Signing Contract...' : 'Sign Contract'}
          </button>

          {error && (
            <p style={{
              color: '#dc3545',
              marginTop: '20px',
              padding: '15px',
              backgroundColor: '#f8d7da',
              borderRadius: '6px'
            }}>
              {error}
            </p>
          )}
        </div>

        {/* Help Text */}
        <p style={{
          textAlign: 'center',
          color: '#666',
          fontSize: '14px',
          marginTop: '30px'
        }}>
          Questions? Contact us at{' '}
          <a href="mailto:relations@valleyeats.ca" style={{ color: '#79A763' }}>
            relations@valleyeats.ca
          </a>
          {' '}or call <strong>1-855-610-3287</strong>
        </p>
      </div>
      <Footer />
    </>
  );
};

// Contract content component with full contract text
const ContractContent: React.FC<{ fullName: string }> = ({ fullName }) => {
  return (
    <div style={{ lineHeight: '1.8', color: '#333' }}>
      <p style={{ marginBottom: '20px' }}>
        <strong style={{ fontSize: '16px' }}>Welcome {fullName} to Valley Eats!</strong> We are pleased to offer you a contract position as a Delivery Driver. This agreement outlines the terms, responsibilities, compensation, and performance expectations of your role.
      </p>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>1. Position and Reporting</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li>You are engaged as an <strong>Independent Contractor</strong> in the role of Delivery Courier</li>
        <li>You will report to the Courier Relations Coordinator</li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>2. Compensation</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li><strong>Base Pay:</strong> $4.00 per order</li>
        <li><strong>Performance Bonus:</strong> Up to $0.50 per delivery for completing runs within 5 minutes of the system's estimated travel time (wait times excluded)</li>
        <li><strong>Tips:</strong> 100% of tips received on deliveries will be paid to you</li>
        <li><strong>Guaranteed Minimum Wage Protection:</strong> You are guaranteed Ontario minimum wage for all active delivery time, averaged over a pay-period
          <ul style={{ paddingLeft: '25px', marginTop: '8px' }}>
            <li>Active time starts when you mark "En Route" and ends when you mark "Delivered"</li>
            <li>Minimum wage compliance is calculated across each pay period</li>
            <li>If your delivery earnings fall below the minimum wage during this time, a top-up will be automatically applied</li>
          </ul>
        </li>
        <li style={{ marginTop: '8px', fontStyle: 'italic', fontSize: '14px' }}><strong>Note:</strong> Compensation is inclusive of tax, and as an independent contractor, you are responsible for remitting any applicable taxes to the appropriate authorities.</li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>3. Contract Terms</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li>This is a contract position with a <strong>90-day probationary period</strong></li>
        <li>You must provide 24 hours' notice to drop a scheduled shift unless due to illness, family emergency, or other extenuating circumstances</li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>4. Independent Contractor Status</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li>You are not an employee of Valley Eats</li>
        <li>You are not eligible for employee benefits (e.g., health insurance, vacation pay)</li>
        <li>You have no authority to bind or represent the Company</li>
        <li>This agreement does not create an employer-employee relationship</li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>5. Market Flexibility</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li>You may be requested to work in alternate delivery zones based on demand</li>
        <li>If reassigned, you may receive driver points or financial compensation</li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>6. Order Assignment System</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li>Orders are assigned based on:
          <ul style={{ paddingLeft: '25px', marginTop: '8px' }}>
            <li>Distance to pickup</li>
            <li>Route efficiency</li>
            <li>Acceptance rate</li>
            <li>Error rate</li>
            <li>Smart bundling</li>
          </ul>
        </li>
        <li style={{ marginTop: '8px' }}><strong>Important:</strong> Background location must be enabled to receive offers</li>
        <li>Orders are assigned just-in-time before heading to pickup</li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>7. Performance-Based Rewards</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li><strong>Points System:</strong> Earn points for positive customer ratings</li>
        <li><strong>No point deductions:</strong> Your account status cannot be downgraded due to performance</li>
        <li>Points can only increase, safeguarding your ability to receive shift slots and perks</li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>8. Transparent Earnings Display</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li><strong>During assignment:</strong> Trip Pay (base + distance) is shown</li>
        <li><strong>Tips:</strong> Displayed only after delivery in accordance with Bill 88</li>
        <li><strong>After delivery:</strong> Complete earnings breakdown is available in-app</li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>9. Required Equipment</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li>You must use a Valley Eats-approved insulated bag</li>
        <li>Purchase available for <strong>$45.00</strong>, deducted from your first payout</li>
        <li>If you withdraw from the contract and do not return the bag, a <strong>$60.00 fee</strong> will apply</li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>10. Delivery Requirements</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li>A reliable motor-vehicle</li>
        <li>Maintain a valid driver's license</li>
        <li>Ensure your vehicle is insured</li>
        <li>Correct delivery of confirmed items is your responsibility
          <ul style={{ paddingLeft: '25px', marginTop: '8px' }}>
            <li>You may be liable for costs due to incorrect deliveries</li>
          </ul>
        </li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>11. Access to Courier Portal – drivers.valleyeats.ca</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li>Access your performance metrics, bonus status, and delivery history</li>
        <li>Submit and track disputes</li>
        <li>Monitor your Bill 88 compliance</li>
        <li>Access performance tips and suggestions</li>
        <li>Review all timer logs and run history</li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>12. Dispute Resolution</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li>Submit disputes through the Courier Portal</li>
        <li>A dedicated review team will ensure a timely and transparent process</li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>13. Comprehensive Audit Protection</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li>All delivery and earnings data stored securely for 3+ years</li>
        <li>Transparent payment calculations</li>
        <li>Protection against unjust deactivation</li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>14. At-Will Agreement</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li>This agreement is at-will, meaning either party may terminate at any time, with or without cause</li>
        <li>Valley Eats may update compensation or terms on a prospective basis with notice</li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>15. Additional Agreements</h3>
      <ul style={{ paddingLeft: '25px' }}>
        <li>You may be asked to sign confidentiality agreements</li>
        <li>You may be asked to sign intellectual property or data use agreements</li>
      </ul>

      <h3 style={{ color: '#79A763', marginTop: '25px', marginBottom: '10px' }}>16. Entire Agreement</h3>
      <p>This document represents the entire agreement between you and Valley Eats. Any changes must be made in writing and signed by both you and an authorized Company representative.</p>

      <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#f0f8ec', borderLeft: '4px solid #79A763' }}>
        <p style={{ margin: '0', fontWeight: 'bold', marginBottom: '10px' }}>✅ Action Items for You:</p>
        <ul style={{ paddingLeft: '25px', margin: '10px 0 0 0' }}>
          <li>Enable Background Location to receive orders</li>
          <li>Monitor your performance to maximize bonuses</li>
          <li>Stay active and responsive during deliveries for accurate time tracking</li>
        </ul>
      </div>
    </div>
  );
};

export default SignContract;
